import { useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ListIcon from "@mui/icons-material/List";
import { HistoryOutlined, WorkOutline } from "@mui/icons-material";
import SchemaIcon from "@mui/icons-material/Schema";
import BarChartIcon from "@mui/icons-material/BarChart";
import { useUserContext } from "../../contexts/userContext";
import { buildPermissions } from "../../auth/permissions";

const Item = ({ title, to, icon, sidebarForeground }) => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const location = useLocation();

	return (
		<MenuItem
			active={location.pathname === to}
			style={{
				color: sidebarForeground,
			}}
			onClick={() => {}}
			icon={icon}
		>
			<Typography 
				sx={{ 
					whiteSpace: "nowrap",
					overflow: "visible",
					textOverflow: "clip"
				}}
			>
				{title}
			</Typography>
			<Link to={to} />
		</MenuItem>
	);
};

const Sidebar = () => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const sidebarForeground =
		theme.palette.mode === "dark" ? colors.grey[100] : "#f8fafc";
	const [isCollapsed, setIsCollapsed] = useState(false);
	const userContext = useUserContext();
	const user = userContext.getUser();

	const hasRole = (role) => !!user && typeof user.hasRole === "function" && user.hasRole(role);
	const { canViewBusinessDataView, canViewOperationalDataView } = buildPermissions(hasRole);

	return (
		<Box
			sx={{
				"& .pro-sidebar-inner": {
					background: `${colors.primary[400]} !important`,
				},
				"& .pro-icon-wrapper": {
					backgroundColor: "transparent !important",
				},
				"& .pro-inner-item": {
					padding: "10px 35px 5px 5px !important",
					overflow: "visible !important",
				},
				"& .pro-inner-item > span": {
					overflow: "visible !important",
					textOverflow: "clip !important",
					whiteSpace: "nowrap !important",
				},
				"& .pro-inner-item:hover": {
					color: `${colors.blueAccent[400]} !important`,
				},
				"& .pro-menu-item.active": {
					color: `${colors.blueAccent[300]} !important`,
				},
				"& .pro-menu-item:active": {
					color: `${colors.blueAccent[500]} !important`,
				},
				"& .pro-sidebar.collapsed .pro-inner-item p": {
					display: "none !important",
				},
				"& .pro-sidebar.collapsed .pro-inner-item": {
					padding: "10px !important",
					justifyContent: "center !important",
					display: "flex !important",
					alignItems: "center !important",
				},
				"& .pro-sidebar.collapsed .pro-icon-wrapper": {
					display: "flex !important",
					justifyContent: "center !important",
					alignItems: "center !important",
					margin: "0 auto !important",
				},
				"& .pro-sidebar.collapsed .pro-menu-item": {
					display: "flex !important",
					justifyContent: "center !important",
					alignItems: "center !important",
				},
			}}
		>
			<ProSidebar collapsed={isCollapsed} width="260px">
				<Menu iconShape="square">
					{/* LOGO AND MENU ICON */}
					<MenuItem
						onClick={() => setIsCollapsed(!isCollapsed)}
						icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
						style={{
							margin: "5px 0 10px 0",
							color: sidebarForeground,
						}}
					>
						{!isCollapsed && (
							<Box
								display="flex"
								alignItems="center"
								justifyContent="flex-end"
								width="100%"
								pr={1}
							>
								<IconButton
									onClick={(event) => {
										event.stopPropagation();
										setIsCollapsed(true);
									}}
									sx={{ color: sidebarForeground }}
									aria-label="Collapse sidebar"
								>
									<MenuOutlinedIcon />
								</IconButton>
							</Box>
						)}
					</MenuItem>
					<Box paddingLeft={isCollapsed ? undefined : "10%"}>
						<>
							<Item title="Dashboard" to="/" icon={<HomeOutlinedIcon />} sidebarForeground={sidebarForeground} />
							<Item title="Sales Orders" to="/salesorders" icon={<ReceiptIcon />} sidebarForeground={sidebarForeground} />
							<Item title="Work Orders" to="/workorders" icon={<WorkOutline />} sidebarForeground={sidebarForeground} />
							<Item title="Workflows" to="/workflows" icon={<AccountTreeIcon />} sidebarForeground={sidebarForeground} />
							<Item title="Wizard" to="/wizard" icon={<AutoFixHighIcon />} sidebarForeground={sidebarForeground} />
							<Item title="Events" to="/events" icon={<HistoryOutlined />} sidebarForeground={sidebarForeground} />
							<Item title="Logs" to="/logs" icon={<ListIcon />} sidebarForeground={sidebarForeground} />
							{hasRole("ngom-administrator") && (
								<Item title="Administration" to="/administration" icon={<AdminPanelSettingsIcon />} sidebarForeground={sidebarForeground} />
							)}
							{canViewOperationalDataView && (
								<Item title="Operative Data View" to="/operational-dataview" icon={<BarChartIcon />} sidebarForeground={sidebarForeground} />
							)}
							{canViewBusinessDataView && (
								<Item title="Business Data View" to="/business-dataview" icon={<BarChartIcon />} sidebarForeground={sidebarForeground} />
							)}
							{hasRole("ngom-businessuser") && (
								<Item title="Dag Templates" to="/dag_templates" icon={<SchemaIcon />} sidebarForeground={sidebarForeground} />
							)}
						</>
					</Box>
				</Menu>
			</ProSidebar>
		</Box>
	);
};

export default Sidebar;
